import { ApolloError } from '@apollo/client'

export const UNAUTHORIZED_ERROR_CODE = 'unauthorized'

/**
 * O core responde `unauthorized` quando a matrícula do aluno não dá acesso ao
 * curso — por exemplo, quando o produtor tira um curso da trilha depois que ele
 * já tinha começado a assistir. É um erro permanente: mandar "tente novamente
 * mais tarde" faz o aluno recarregar a página para sempre.
 */
export const isUnauthorizedError = (error?: unknown): boolean =>
  Boolean(
    (error as ApolloError)?.graphQLErrors?.some(
      (graphQLError) =>
        graphQLError.extensions?.code === UNAUTHORIZED_ERROR_CODE,
    ),
  )

export const UNAUTHORIZED_MUTATION_ERROR_CODE = 'unauthorized_mutation'

/**
 * `unauthorized_mutation` é a recusa de ESCRITA do core (ex.: "Not permitted to
 * update this LessonProgress"). A resposta é a mesma em toda tentativa para a
 * mesma matrícula/card, então não adianta reenviar.
 */
export const isUnauthorizedMutationError = (error?: unknown): boolean =>
  Boolean(
    (error as ApolloError)?.graphQLErrors?.some(
      (graphQLError) =>
        graphQLError.extensions?.code === UNAUTHORIZED_MUTATION_ERROR_CODE,
    ),
  )

export const EMPTY_PAGE_ERROR_CODE = 'empty_page'

/**
 * `Empty page!` é a resposta do core quando o offset passa do fim da lista
 * (`Helpers::PaginationHelper`) — o único erro que significa "acabou". Qualquer
 * outro erro é transitório (rede, timeout, 5xx) e NÃO pode encerrar a
 * paginação, senão um blip de rede trunca a lista até o aluno recarregar.
 *
 * A decisão sai do `extensions.code`, não da mensagem: `ApolloError.message` é
 * a JUNÇÃO por `\n` de todos os erros da resposta
 * (`errors.map((e) => e.message).join('\n')`), então qualquer erro
 * acompanhante — outro campo paginado do mesmo documento, um `networkError` —
 * faz a comparação por string falhar e a lista nunca encerrar. A comparação
 * por mensagem fica como fallback para erro cru, sem `graphQLErrors`.
 */
export const isEmptyPageError = (error?: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false

  const hasEmptyPageCode = (error as ApolloError).graphQLErrors?.some(
    (graphQLError) => graphQLError.extensions?.code === EMPTY_PAGE_ERROR_CODE,
  )

  return Boolean(
    hasEmptyPageCode ||
      (error as { message?: unknown }).message === 'Empty page!',
  )
}
